import * as React from 'react'

import { KaraokeChunk } from '../../../Shared/components/readAloud/readAloudKaraoke'
import { ReadAloudPlayerBar } from '../../../Shared/components/readAloud/readAloudPlayerBar'
import { useReadAloud } from '../../../Shared/components/readAloud/readAloudContext'
import useReadAloudPlayer from '../../../Shared/hooks/readAloud/useReadAloudPlayer'
import { joinWords } from '../../helpers/joinWords'
import { WordObject } from '../../interfaces/proofreaderActivities'

export const PASSAGE_ID = 'proofreader-passage'

const underlineMarkup = (paragraph: WordObject[], underlineErrors: boolean) => (text: string, index: number) => (underlineErrors && paragraph[index].underlined ? `<u>${text}</u>` : text)

export const passageChunks = (passage: WordObject[][], underlineErrors = false) => passage.map(paragraph => ({ html: joinWords(paragraph.map(word => word.currentText), underlineMarkup(paragraph, underlineErrors)), }))

const ReadAloudPassageRegion = ({ children, }: { children: React.ReactNode }) => {
  const { readAloudEnabled, } = useReadAloud()

  if (!readAloudEnabled) { return children }

  return <div id={PASSAGE_ID}>{children}</div>
}

const ReadAloudPassage = ({ onExit, passage, renderActionsBar, underlineErrors, }: { onExit: () => void, passage: WordObject[][], renderActionsBar: (player: React.ReactNode) => React.ReactNode, underlineErrors: boolean }) => {
  const chunks = React.useMemo(() => passageChunks(passage, underlineErrors), [passage, underlineErrors])
  const { activeChunkIndex, activeWordIndex, cycleRate, pause, play, prepared, rate, rewind, status, stop, } = useReadAloudPlayer({ chunks, onFinish: onExit, })
  const autoPlayedRef = React.useRef(false)

  React.useEffect(() => {
    if (!prepared || autoPlayedRef.current) { return }

    autoPlayedRef.current = true
    play()
  }, [prepared])

  const handleExit = () => {
    stop()
    onExit()
  }

  const playerBar = (
    <ReadAloudPlayerBar
      controlsId={PASSAGE_ID}
      cycleRate={cycleRate}
      onExit={handleExit}
      pause={pause}
      play={play}
      rate={rate}
      rewind={rewind}
      status={status}
    />
  )

  return (
    <div className="read-aloud-passage">
      <p className="sr-only">Read aloud is on. Editing is paused until you stop listening.</p>
      {renderActionsBar(playerBar)}
      <div className="editor">
        {chunks.map((chunk, index) => (
          <KaraokeChunk
            className={index === activeChunkIndex ? 'karaoke-active-chunk' : undefined}
            html={chunk.html}
            key={index}
            spokenWordIndex={index === activeChunkIndex ? activeWordIndex : null}
          />
        ))}
      </div>
    </div>
  )
}

export { ReadAloudPassage, ReadAloudPassageRegion }
